import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Card, Form, Layout, PageHeader, Space } from "antd";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";

import { CompositionForm } from "./Form";

const materialCompositionQuery = gql`
  query ($id: ID!, $pagination: ConnectionPagination, $sorting: MaterialCompositionGroupSorting, $searchQuery: String) {
    materialComposition(id: $id) {
      id
      code
      nameIt
      nameEn
      descriptionIt
      descriptionEn
      compositionGroups {
        id
        code
      }
    }
    materialCompositionGroups(pagination: $pagination, sorting: $sorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        nameIt
        nameEn
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const updateMaterialCompositionMutation = gql`
  mutation ($id: ID!, $input: UpdateMaterialCompositionInput!) {
    updateMaterialComposition(id: $id, input: $input) {
      composition {
        id
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const Edit: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    error,
    loading: queryLoading,
    data,
  } = useQuery(materialCompositionQuery, {
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  const [execMutation, { loading }] = useMutation(updateMaterialCompositionMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: graphQLError.name, errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate("../../");
    },
  });

  const submit = (input: any) => {
    execMutation({ variables: { id, input } });
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="../../">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const resource = data.materialComposition;
  const values = {
    ...resource,
    compositionCompositionGroups: resource.compositionGroups?.map((group: any) => group.id),
  };

  return (
    <Fragment>
      <Layout className="content">
        <PageHeader
          ghost={true}
          title={`Edit Composition ${values.code}`}
          extra={
            <Space size="large">
              <Button onClick={() => navigate("../..")}>Cancel</Button>
              <Button type="primary" loading={loading} onClick={() => form.submit()}>
                Save
              </Button>
            </Space>
          }
        />
        <Layout.Content className="content">
          <Card>
            {otherErrors.length > 0 && (
              <Space direction="vertical" style={{ marginBottom: "10px" }}>
                {otherErrors.map((otherError, index) => (
                  <Alert key={index} type="error" message="Error" description={otherError} />
                ))}
              </Space>
            )}
            <CompositionForm form={form} values={values} data={data} submit={submit} />
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
